<template>
  <section>
    <Card v-if="user" :no-card="noCard">
      <Spinner v-if="loading" />
      <FormGrouping v-else-if="user">
        <ApiKey no-settings-link size="md" />
        <FormGroup
          data-id="email-address"
          :data-value="user.emailAddress"
          :label="'Email address'"
        >
          <EmailAddress :value="user.emailAddress" />
        </FormGroup>
        <FormGroup
          v-if="user.vendorEmailAddress"
          data-id="vendor-address"
          :label="'Vendor address'"
        >
          <EmailAddress :value="user.vendorEmailAddress" />
        </FormGroup>
        <FormGroup data-id="id" :label="'Account ID'">
          <UUID :value="user.id" />
        </FormGroup>
        <FormGroup
          v-if="hasSocialProvider"
          data-id="sso-provider"
          :label="'Federation'"
          :value="user.ssoProvider || ''"
        />

        <FormGroup
          v-if="subscription"
          label="Customer ID"
          :value="subscription.customerId || ''"
          data-id="plan-customer-id"
        />
        <FormGroup
          v-if="!isChild && !hasSocialProvider"
          data-id="password"
          :label="'Password'"
        >
          <span data-id="account-has-subscription-password-options">
            <Btn
              tag="button"
              outline
              :variant="Variant.secondary"
              data-id="set-password-button"
              size="sm"
              @click="setPassword"
            >
              <span v-if="hasPassword">{{ 'Reset password' }}</span>
              <span v-else>{{ 'Set password for account' }}</span>
            </Btn>
          </span>
        </FormGroup>
        <section v-if="isChild">
          <FormGroup
            :label="'Organization ID'"
            data-id="application-organization-id"
          >
            <UUID :value="user.organization" />
          </FormGroup>
        </section>
        <More text="Misc settings">
          <FormRow :label="'Save contacts'" data-id="add-new-contacts">
            <FormCheckbox
              v-model="addNewContacts"
              switch
              data-id="toggle-add-new-contacts"
              @change="toggleAddNewContacts"
            />
          </FormRow>
          <FormGroup
            data-id="toggle-onboarding"
            :data-show-onboarding="showOnboarding ? '1' : '0'"
            label="Reset onboarding"
          >
            <Btn
              tag="button"
              size="sm"
              outline
              data-id="toggle-onboarding"
              :variant="Variant.secondary"
              @click="toggleOnboarding"
            >
              Reset
            </Btn>
          </FormGroup>
        </More>
        <section v-if="subscription && !isChild">
          <More text="Subscription details">
            <FormGroup
              :label="'Subscription ID'"
              data-id="application-subscription-id"
            >
              {{ subscription.subscriptionId }}
            </FormGroup>
            <FormGroup :label="'Customer ID'" data-id="application-customer-id">
              {{ subscription.customerId || '-' }}
            </FormGroup>
            <FormGroup
              :label="'Cancels at date'"
              data-id="application-cancel-at"
              :data-cancels-at="
                subscription.cancelAt
                  ? subscription.cancelAt.toString()
                  : undefined
              "
            >
              {{ subscription.cancelAt || '-' }}
            </FormGroup>
            <FormGroup
              :label="'Trial end date'"
              data-id="application-trial-end"
              last
            >
              {{ subscription.trialEnd || '-' }}
            </FormGroup>
          </More>
        </section>
      </FormGrouping>

      <template #footer>
        <div>
          <FormDropDown position="top-right">
            <template #button-content>
              <span class="account-edit-settings p-0">
                <span data-id="account-edit-settings">{{
                  'Edit account'
                }}</span>
              </span>
            </template>
            <FormDropDownItem
              v-if="!isChild"
              ref="upgradeAccount"
              href="/upgrade"
            >
              <span data-id="account-action-upgrade-account">{{
                'Upgrade account'
              }}</span>
            </FormDropDownItem>
            <FormDropDownItem
              button
              data-id="change-email-dropdown-item"
              @click="requestEmailChange"
            >
              <span data-id="account-action-change-email">{{
                'Change email address'
              }}</span>
            </FormDropDownItem>
            <FormDropDownItem button @click="requestDeleteAccount">
              <span data-id="account-action-delete-account">{{
                'Delete account'
              }}</span>
            </FormDropDownItem>
          </FormDropDown>
        </div>
      </template>
    </Card>
  </section>
</template>

<script setup lang="ts">
import FormDropDown from '../components/form/FormDropDown.vue';
import FormDropDownItem from '../components/form/FormDropDownItem.vue';
import FormGroup from './form/FormGroup.vue';
import type { OffboardingAction } from '../lib/services/helpers';
import { loadFetch, loadFetchSeq } from '../lib/services/helpers';
import Spinner from '../components/Spinner.vue';
import EmailAddress from '../components/form/EmailAddress.vue';
import UUID from '../components/form/UUID.vue';
import More from '../components/More.vue';
import { onMounted, ref, computed } from 'vue';
import FormGrouping from '../components/form/FormGrouping.vue';
import { useStore3 } from '../store/main';
import { storeToRefs } from 'pinia';
import FormCheckbox from '../components/form/FormCheckbox.vue';
import FormRow from '../components/form/FormRow.vue';
import Card from '../components/tw/Card.vue';
import Btn from '../components/form/Btn.vue';
import { Variant } from '../store/types';
import ApiKey from '../components/form/ApiKey.vue';
import { navigateTo } from '#imports';

const upgradeAccount = ref(null);
const store3 = useStore3();

const loading = ref(false);
const addNewContacts = ref(false);
const showOnboarding = ref(false);

withDefaults(defineProps<{ noCard?: boolean }>(), {
  noCard: false,
});
const { subscription, user, isChild, offboardingAction } = storeToRefs(store3);

const hasPassword = computed<boolean>(() => !!user.value?.hasPassword);
const hasSocialProvider = computed<boolean>(() => !!user.value?.ssoProvider);

const toggleAddNewContacts = async () => {
  await loadFetch(
    () => store3.addNewContacts({ value: addNewContacts.value }),
    store3,
    loading,
  );
};

const toggleOnboarding = async () => {
  await loadFetchSeq(
    [() => store3.deleteAllOnboarding(), () => store3.getSubscription()],
    store3,
    loading,
  );
};

onMounted(async () => {
  await loadFetch(() => store3.getSubscription(), store3, loading);
  addNewContacts.value = user.value?.addNewContacts ?? false;
  showOnboarding.value = user.value?.hasOnboarded ?? false;
});

const requestEmailChange = async () => {
  const newEmail = window.prompt('Please enter your new email address');
  if (
    newEmail &&
    window.confirm(
      `Are you sure you want to change your account email address to ${newEmail}?`,
    )
  ) {
    try {
      loading.value = true;
      await store3.changeEmailAddress({
        newEmailAddress: newEmail,
      });
      loading.value = false;
    } catch (e) {
      store3.setError({
        error: e,
      });
      loading.value = false;
    }
  }
};

const requestDeleteAccount = async () => {
  const p: OffboardingAction = 'delete-account';
  offboardingAction.value = p;
  await navigateTo('/offboarding');
};

const setPassword = async () => {
  const password = prompt('Enter new password for account' as string);
  if (password) {
    await loadFetch(() => store3.setPassword({ password }), store3, loading);
    store3.setFlash({
      text: 'Password changed',
      variant: Variant.success,
    });
  }
};
</script>
